import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { Button, Input, Collapse, Table, Dropdown } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { v4 as uuid } from "uuid";

const PreselectedListsPanel = ({
  productCategory,
  listOptions,
  fullLists,
  testContOptions,
  maxAllOptions,
  onAddToMainList,
  selectedBrand,
}) => {
  
  const [lists, setLists] = useState([]); // data from API
  const [preselectedLists, setPreselectedLists] = useState([]); // cards we show
  const didInitRef = useRef(false); // ensure we seed once
  const dropdownTriggerRef = useRef(null);
  const [products, setProducts] = useState([]);
 
 
  useEffect(() => {
    if (didInitRef.current || !listOptions?.length) return;
    const startPerformance = 10;
    const initialCards = listOptions.map((list, idx) => ({
      id: uuid(),
      listOption: list,
      segmentOption: null,
      priority: String(idx + 1),
      testCont: { value: "Cont.", label: "Cont." },
      orderQty: "",
      maxAll: { value: "All", label: "All" },
      comment: "",
      segmentOptions: [],
      productCategory: "",
      performance: String(startPerformance - idx),
      cost: "",
      source_code: "",
    }));
    setPreselectedLists(initialCards);
    didInitRef.current = true; // never run again unless we explicitly reset
  }, [listOptions]);

 
  useEffect(() => {
    console.log("Full lists from parent:", fullLists);
  }, [fullLists]);
 

  useEffect(() => {
    if (!selectedBrand) return;

    (async () => {
      try {
        const { getProductsByBrand } = await import(
          "../../../core/api/products"
        );
        const res = await getProductsByBrand(selectedBrand);
        const fetchedProducts = res.data;
        setProducts(fetchedProducts);
        console.log("Fetched products by brand:", fetchedProducts);

        // Auto-fill productCategory in cards if empty
        setPreselectedLists((prev) =>
          prev.map((card) => {
            if (!card.productCategory && fetchedProducts.length > 0) {
              return { ...card, productCategory: fetchedProducts[0].id };
            }
            return card;
          })
        );
      } catch (err) {
        console.error("Failed to fetch products by brand:", err);
      }
    })();
  }, [selectedBrand]);

  const productOptions = products.map((p) => ({
    value: p.id,
    label: p.name,
  }));

  useEffect(() => {
    (async () => {
      try {
        const response = await import("../../../core/api/mediaplan").then((m) =>
          m.getLists()
        );
        setLists(response.data);
        console.log("Fetched lists data:", response.data);
      } catch (err) {
        console.error("Error fetching lists:", err);
      }
    })();
  }, []);

  /**
   * ──────────────────────────────────────────────────────────────────────────
   *  ENRICH EACH CARD WITH SEGMENT OPTIONS WHEN API DATA ARRIVES
   * ──────────────────────────────────────────────────────────────────────────
   */
  useEffect(() => {
    if (!lists.length) return;
    setPreselectedLists((prev) =>
      prev.map((card) => {
        if (!card.listOption) return card;
        const full = lists.find((l) => l.id === card.listOption.value);
        const segs =
          full?.segments?.map((s) => ({ value: s.id, label: s.name })) ?? [];
        return { ...card, segmentOptions: segs };
      })
    );
  }, [lists]);

 
  const handlePreselectedListChange = (index, field, value) => {
    setPreselectedLists((prev) => {
      const next = [...prev];
      if (field === "listOption") {
        const full = value ? lists.find((l) => l.id === value.value) : null;
        const segs =
          full?.segments?.map((s) => ({ value: s.id, label: s.name })) ?? [];
        const productCategory = full?.product_category || "";

        next[index] = {
          ...next[index],
          [field]: value,
          segmentOption: null,
          segmentOptions: segs,
          productCategory,
        };
      } else {
        next[index] = { ...next[index], [field]: value };
      }
      return next;
    });
  };

 
  const handleAddPreselectedList = (index) => {
    const item = preselectedLists[index];
    if (!item.listOption) return; // Only check for listOption

    const fullList = lists.find((l) => l.id === item.listOption.value);
    if (!fullList) return;

    // Initialize segment-related variables
    let segmentData = {
      segmentOption: null,
      period: "",
      quantity: 0,
      category: "",
      date_released: "-",
    };

    // If segment is selected, get its details
    if (item.segmentOption) {
      const seg = fullList.segments.find(
        (s) => s.id === item.segmentOption.value
      );
      if (seg) {
        segmentData = {
          segmentOption: item.segmentOption,
          period: seg.period || "",
          quantity: seg.quantity || 0,
          category: seg.category || "",
          date_released: seg.date_released || "-",
        };
      }
    }

    const payload = {
      listOption: item.listOption,
      segmentOption: segmentData.segmentOption,
      priority: item.priority,
      testCont: item.testCont,
      orderQty: item.orderQty,
      maxAll: item.maxAll,
      comment: item.comment,
      source_code: item.source_code,
      productCategory: item.productCategory,
      performance: item.performance,
      cost: item.cost,
      period: segmentData.period,
      quantity: segmentData.quantity,
      category: segmentData.category,
      date_released: segmentData.date_released,
    };

    // 🔑  Remove from local state *before* notifying parent to avoid flashes
    setPreselectedLists((prev) => prev.filter((_, i) => i !== index));

    // 🚚  Push up to parent
    onAddToMainList(payload);
  };

  /**
   * ──────────────────────────────────────────────────────────────────────────
   *  TABLE COLUMNS CONFIGURATION
   * ──────────────────────────────────────────────────────────────────────────
   */
  const columns = [
    {
      title: "Source Code",
      dataIndex: "source_code",
      key: "source_code",
      width: 150,
      render: (_, record, index) => (
        <Input
          value={record.source_code}
          onChange={(e) =>
            handlePreselectedListChange(index, "source_code", e.target.value)
          }
          placeholder="Source Code"
        />
      ),
    },
    {
      title: "List",
      dataIndex: "listOption",
      key: "listOption",
      width: 200,
      render: (_, record, index) => (
        <Select
          options={listOptions}
          value={record.listOption}
          onChange={(v) => handlePreselectedListChange(index, "listOption", v)}
          placeholder="Select List"
          styles={{
            container: (base) => ({ ...base, width: "100%" }),
            control: (base) => ({ ...base, minWidth: 0 }),
            menuPortal: (base) => ({ ...base, zIndex: 2000 }),
          }}
          isDisabled={true}
          menuPortalTarget={document.body}
        />
      ),
    },
    {
      title: "Segment",
      dataIndex: "segmentOption",
      key: "segmentOption",
      width: 200,
      render: (_, record, index) => (
        <Select
          options={record.segmentOptions}
          value={record.segmentOption}
          onChange={(v) =>
            handlePreselectedListChange(index, "segmentOption", v)
          }
          placeholder="Select Segment"
          isDisabled={!record.listOption}
          styles={{
            container: (base) => ({ ...base, width: "100%" }),
            control: (base) => ({ ...base, minWidth: 0 }),
            menuPortal: (base) => ({ ...base, zIndex: 2000 }),
          }}
          menuPortalTarget={document.body}
        />
      ),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      width: 100,
      render: (_, record, index) => (
        <Input
          value={record.priority}
          onChange={(e) =>
            handlePreselectedListChange(index, "priority", e.target.value)
          }
          placeholder="Priority"
        />
      ),
    },
    {
      title: "Test/Cont.",
      dataIndex: "testCont",
      key: "testCont",
      width: 120,
      render: (_, record, index) => (
        <Select
          options={testContOptions}
          value={record.testCont}
          onChange={(v) => handlePreselectedListChange(index, "testCont", v)}
          placeholder="Test/Cont."
          styles={{
            container: (base) => ({ ...base, width: "100%" }),
            control: (base) => ({ ...base, minWidth: 0 }),
            menuPortal: (base) => ({ ...base, zIndex: 2000 }),
          }}
          menuPortalTarget={document.body}
        />
      ),
    },
    {
      title: "Order Qty",
      dataIndex: "orderQty",
      key: "orderQty",
      width: 100,
      render: (_, record, index) => (
        <Input
          type="number"
          value={record.orderQty}
          onChange={(e) =>
            handlePreselectedListChange(index, "orderQty", e.target.value)
          }
          placeholder="Order Qty"
        />
      ),
    },
    {
      title: "Max/All",
      dataIndex: "maxAll",
      key: "maxAll",
      width: 120,
      render: (_, record, index) => (
        <Select
          options={maxAllOptions}
          value={record.maxAll}
          onChange={(v) => handlePreselectedListChange(index, "maxAll", v)}
          placeholder="Max/All"
          styles={{
            container: (base) => ({ ...base, width: "100%" }),
            control: (base) => ({ ...base, minWidth: 0 }),
            menuPortal: (base) => ({ ...base, zIndex: 2000 }),
          }}
          menuPortalTarget={document.body}
        />
      ),
    },
    {
      title: "Comment",
      dataIndex: "comment",
      key: "comment",
      width: 150,
      render: (_, record, index) => (
        <Input
          value={record.comment}
          onChange={(e) =>
            handlePreselectedListChange(index, "comment", e.target.value)
          }
          placeholder="Comment"
        />
      ),
    },

    {
      title: "Product Category",
      dataIndex: "productCategory",
      key: "productCategory",
      width: 200,
      render: (_, record, index) => (
        <Input
          value={productCategory}
          placeholder="Select Product"
          style={{
            backgroundColor: "#fafafa",
            cursor: "not-allowed",
            width: "100%",
          }}
        />
      ),
    },
    {
      title: "Performance",
      dataIndex: "performance",
      key: "performance",
      width: 150,
      render: (_, record, index) => (
        <Input
         readOnly
          value={record.performance}
          onChange={(e) =>
            handlePreselectedListChange(index, "performance", e.target.value)
          }
          placeholder="Performance"
        />
      ),
    },
    {
      title: "Cost",
      dataIndex: "cost",
      key: "cost",
      width: 120,
      render: (_, record, index) => (
        <Input
          type="number"
          step="0.01"
          value={record.cost !== null && record.cost !== undefined && record.cost !== "" ? record.cost : "0.00"}
          onChange={(e) =>
            handlePreselectedListChange(index, "cost", e.target.value)
          }
          placeholder="Cost"
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, record, index) => (
        <Button
          type="primary"
          onClick={() => handleAddPreselectedList(index)}
          icon={<PlusOutlined />}
        />
      ),
    },
  ];

  // Compute a reasonable minimum width for the table based on declared column widths
  const minTableWidth = columns.reduce(
    (sum, col) => sum + (typeof col.width === "number" ? col.width : 120),
    0
  );

  
  return (
    <Collapse defaultActiveKey={[]} style={{ marginBottom: 16 }}>
      <Collapse.Panel
        header={`Top performance Lists – ${productCategory || ""}`}
        key="preselected-lists"
        style={{ minWidth: 0 }}
      >
        <div style={{ width: "100%", overflowX: "auto" }}>
          <div style={{ display: "inline-block", minWidth: minTableWidth }}>
            <Table
              dataSource={preselectedLists.map((item) => ({
                ...item,
                key: item.id,
              }))}
              columns={columns}
              pagination={false}
              size="small"
              bordered
              className="preselected-lists-table"
            />
          </div>
        </div>
      </Collapse.Panel>
    </Collapse>
  );
};

export default PreselectedListsPanel;
